import {
  type AvailableMoves,
  getMovesWithSteps,
  type Model,
  type MoveInfo,
  playMoves,
  playTurn,
  rollDice as rollDiceCore,
} from '@kfiroo/backgammon-core'
import { last } from 'lodash'

import { createUndoStack, type UndoStack } from './UndoStack'

export interface TurnState {
  model: Model
  moves: MoveInfo[]
  steps: number[]
}

/** Full optimistic-rollback checkpoint: everything a roll/move/undo can mutate. */
export interface TurnCheckpoint {
  state: TurnState
  committed: Model
  stack: TurnState[]
}

export class PlayController {
  private state: TurnState
  /** Turn-start model: committed board + rolled dice, with any liveTurn stripped. */
  private committed: Model
  private readonly undoStack: UndoStack<TurnState> =
    createUndoStack<TurnState>()

  constructor(model: Model) {
    this.committed = stripLiveTurn(model)
    const steps = getOwnSteps(this.committed)
    this.state = { model: this.committed, moves: [], steps }
    if (hasDice(this.committed)) {
      this.snapshot() // initial snapshot (matches the legacy `if (dice) snapshot()`)
      // Hydrate: replay each persisted move so the display board, remaining
      // steps, AND undo stack are rebuilt exactly as if played live. This runs
      // on every round-trip rebuild and on refresh-resume.
      for (const move of liveTurnMoves(model)) {
        this.applyMoves([move])
      }
    }
  }

  get model(): Model {
    return this.state.model
  }

  /**
   * The model to PERSIST: committed board + rolled dice, plus the live turn
   * (moves made so far). The committed board is never replaced by the tentative
   * board — opponents derive the tentative position from `liveTurn.moves`.
   */
  get persistModel(): Model {
    const gs = this.committed.gameState
    if (gs.state !== 'in-progress' || gs.dice === null) {
      return this.committed
    }
    return {
      ...this.committed,
      // copy moves so the persisted model does not alias live controller state
      gameState: { ...gs, liveTurn: { moves: [...this.state.moves] } },
    }
  }

  /**
   * Captures a FULL rollback checkpoint — `state`, `committed`, and a copy of
   * the undo stack — so a failed optimistic roll/move/undo persist can be fully
   * reverted (move/undo push or pop the stack; roll replaces `committed`).
   */
  snapshotState(): TurnCheckpoint {
    return {
      state: this.state,
      committed: this.committed,
      stack: this.undoStack.capture(),
    }
  }

  restoreState(checkpoint: TurnCheckpoint): void {
    this.state = checkpoint.state
    this.committed = checkpoint.committed
    this.undoStack.restore(checkpoint.stack)
  }

  getValidMoves(): AvailableMoves {
    return getMovesWithSteps(this.state.model, this.state.steps)
  }

  /**
   * Resolve a from→to gesture to a move chain (preserving the hit-priority
   * tie-break), then apply it ONE HOP AT A TIME so each hop is its own undo
   * boundary (per-step undo).
   */
  move(from: number, to: number): void {
    const availableMoves = this.getValidMoves()
    const movesList = availableMoves[from]
    const sortedMoveList = movesList
      .filter((moves) => last(moves)?.to === to)
      .sort((a, b) =>
        a.some((m) => m.isHit) ? -1 : b.some((m) => m.isHit) ? 1 : 0,
      )
    // NOTE: no guard if sortedMoveList is empty — preserves the legacy move() quirk.
    const moves = sortedMoveList[0]
    for (const move of moves) {
      this.applyMoves([move])
    }
  }

  canEndTurn(): boolean {
    if (!hasDice(this.state.model)) return false
    if (this.state.steps.length === 0) return true
    return Object.keys(this.getValidMoves()).length === 0
  }

  /**
   * Returns the model to PERSIST at turn end (the committed next-turn state from
   * core `playTurn`, with no liveTurn). May THROW (core `playTurn`) on an
   * incomplete turn — the caller preserves the swallow.
   */
  endTurn(initialModel: Model): Model {
    const model = playTurn(stripLiveTurn(initialModel), this.state.moves)
    if (model.gameState.state === 'in-progress') {
      this.state = { ...this.state, model }
      // The turn is committed: its snapshots belong to a turn that is now over,
      // so they must not keep `canUndo()` true (which would leave the Undo
      // button enabled into the next turn — e.g. a turn ended with a die that
      // could not be played from the bar). A failed optimistic persist still
      // rolls back via restoreState, which restores the pre-endTurn stack.
      this.undoStack.restore([])
    }
    return model
  }

  /** Rolls, captures the committed turn-start model, returns the model to persist. */
  rollDice(): Model {
    const model = stripLiveTurn(rollDiceCore(this.state.model))
    this.committed = model
    this.state = { model, moves: [], steps: getOwnSteps(model) }
    this.snapshot() // post-roll snapshot so the first move is undoable
    return this.persistModel
  }

  canUndo(): boolean {
    return this.undoStack.canUndo()
  }

  undo(): void {
    const prev = this.undoStack.undo()
    if (!prev) return
    this.state = prev
  }

  private applyMoves(moves: MoveInfo[]): void {
    this.snapshot() // snapshot BEFORE applying
    const nextMoves = [...this.state.moves, ...moves]
    const { model, steps } = playMoves(
      this.state.model,
      moves,
      this.state.steps,
    )
    this.state = { model, moves: nextMoves, steps }
  }

  private snapshot(): void {
    this.undoStack.push(this.state) // ref-sharing preserved (quirk)
  }
}

function hasDice(model: Model): boolean {
  return (
    model.gameState.state === 'in-progress' && model.gameState.dice !== null
  )
}

/** Removes the liveTurn field so committed/persisted-history state stays clean. */
function stripLiveTurn(model: Model): Model {
  if (
    model.gameState.state !== 'in-progress' ||
    model.gameState.liveTurn == null
  ) {
    return model
  }
  // spread-then-delete drops the optional field without an unused-binding lint
  const { ...gameState } = model.gameState
  delete gameState.liveTurn
  return { ...model, gameState }
}

function liveTurnMoves(model: Model): MoveInfo[] {
  const gs = model.gameState
  return gs.state === 'in-progress' && gs.liveTurn ? gs.liveTurn.moves : []
}

/** PORT of PlayingGameApi.getOwnSteps. */
function getOwnSteps(model: Model): number[] {
  if (model.gameState.state !== 'in-progress' || !model.gameState.dice)
    return []
  const { die1, die2 } = model.gameState.dice
  return die1 === die2 ? [die1, die1, die1, die1] : [die1, die2]
}
